import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LanguageSwitcherComponent } from '../components/language-switcher/language-switcher.component';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout } from '../store/slices/auth-slice';

const NAV_ITEMS: Array<{
  to: string;
  labelKey: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}> = [
  { to: '/chats', labelKey: 'nav.chats', icon: <ChatOutlinedIcon /> },
  { to: '/presets', labelKey: 'nav.presets', icon: <TuneOutlinedIcon /> },
  { to: '/settings', labelKey: 'nav.settings', icon: <SettingsOutlinedIcon /> },
  {
    to: '/admin',
    labelKey: 'nav.admin',
    icon: <AdminPanelSettingsOutlinedIcon />,
    adminOnly: true,
  },
];

export function AppLayout() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';

  const navItems = useMemo(
    () => NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin),
    [isAdmin],
  );

  const handleLogout = useCallback(() => {
    void dispatch(logout()).then(() => {
      navigate('/login', { replace: true });
    });
  }, [dispatch, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        component="nav"
        sx={{
          width: 64,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          py: 1.5,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {navItems.map((item) => {
          const label = t(item.labelKey);

          return (
            <Tooltip key={item.to} title={label} placement="right">
              <IconButton
                component={NavLink}
                to={item.to}
                aria-label={label}
                sx={{
                  color: 'text.secondary',
                  '&.active': {
                    color: 'primary.main',
                    bgcolor: 'action.selected',
                  },
                }}
              >
                {item.icon}
              </IconButton>
            </Tooltip>
          );
        })}
        <Box
          sx={{
            mt: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <LanguageSwitcherComponent />
          <Tooltip title={t('nav.logout')} placement="right">
            <IconButton
              aria-label={t('nav.logout')}
              onClick={handleLogout}
              sx={{ color: 'text.secondary' }}
            >
              <LogoutOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
